import {bboxPolygon} from '@turf/bbox-polygon';
import {SOURCE_TYPES} from './util.js';

const suggestionSequence = [
  'addressLine',
  'locality',
  'adminDistrict',
  'countryRegion',
];

const BING_BASE_URL = 'https://dev.virtualearth.net/REST/v1';
const BING_LOCATIONS_URL = `${BING_BASE_URL}/Locations`;
const BING_AUTOSUGGEST_URL = `${BING_BASE_URL}/Autosuggest`;

export const geocode = (query, center, apiKey) => {
  let url = '';
  // search by center point if there is a center point but
  //  no query string
  if (center && !query) {
    const ptStr = `${center[1]},${center[0]}`;
    url = `${BING_LOCATIONS_URL}/${ptStr}?key=${apiKey}`;
  } else {
    url = `${BING_LOCATIONS_URL}/?q=${encodeURIComponent(query)}&key=${apiKey}`;
  }
  return fetch(url)
    .then(r => r.json())
    .then(data => {
      if (
        !data.resourceSets ||
        data.resourceSets.length === 0 ||
        data.resourceSets[0].resources.length === 0
      ) {
        // eslint-disable-next-line no-console
        console.error('Bing Geocoder: no results found');
      }

      const resource = data.resourceSets[0]?.resources[0];
      const bbox = [
        resource.bbox[1],
        resource.bbox[0],
        resource.bbox[3],
        resource.bbox[2],
      ];
      return {
        bbox,
        geometry: resource.bbox ? bboxPolygon(bbox).geometry : null,
        properties: resource, // preserve the original results
      };
    });
};

export const getSuggestionDescriptions = suggestion => {
  // Bing will provide the address parts for the suggestion
  // To map to our suggestion format, we will prioritize populating the description and put the remaining parts in descriptionDetails

  // Some suggestions have names (i.e. a named landmark like "Madison Square Garden")
  // For those results, the description is automatically that name.
  let title = suggestion.name || null;
  const descriptionDetails = [];
  const addr = suggestion.address;
  let query = title;

  suggestionSequence.forEach(item => {
    if (addr?.[item]) {
      if (!title) {
        // If description hasn't been set yet (the result doesn't have a name and no earlier part of the sequence was available) set the current part as the description
        title = addr[item];
        query = title;
      } else {
        // All other parts of the address should go into the details array
        // It's up to the implementation to get what they need from the array, i.e. a localized join into a string
        descriptionDetails.push(addr[item]);
        query += ' ' + addr[item];
      }
    }
  });

  return {
    title,
    description: descriptionDetails.join(', '),
    label: [title, ...descriptionDetails].join(', '),
    source: SOURCE_TYPES.BING,
    query,
  };
};

export const getSuggestions = (query, center, apiKey) => {
  const url = `${BING_AUTOSUGGEST_URL}?key=${apiKey}`;
  let params = `&query=${query}&includeEntityTypes=Place,Address`;

  if (center) {
    params = params + `&userLocation=${center[0]},${center[1]},5000`;
  }

  return fetch(`${url}${params}`)
    .then(r => r.json())
    .then(data => {
      const transformed = data.resourceSets?.[0].resources?.[0].value.map(r => {
        const description = getSuggestionDescriptions(r);

        return {
          type: 'suggestion',
          suggestion: r,
          ...description,
        };
      });

      return transformed || [];
    });
};
